import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Upload, CheckCircle, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ANNOUNCEMENT_FILES = [
  { id: 'twominutes_apparatus.mp3', label: '2 Minutes - Apparatus Warmup', type: 'apparatus' },
  { id: 'oneminute_apparatus.mp3', label: '1 Minute - Apparatus Warmup', type: 'apparatus' },
  { id: 'warmupdone.mp3', label: 'Warmup Done - Apparatus', type: 'apparatus' },
  { id: 'twominute_general.mp3', label: '2 Minutes - General Warmup', type: 'general' },
  { id: 'oneminute_general.mp3', label: '1 Minute - General Warmup', type: 'general' },
  { id: 'generalwarmupdone.mp3', label: 'Warmup Done - General', type: 'general' },
];

export default function AnnouncementUploader() {
  const [fileStatus, setFileStatus] = useState({});
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    loadFileStatus();
  }, []);

  const loadFileStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/superadmin/announcement-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFileStatus(response.data.files);
    } catch (error) {
      console.error('Error loading file status:', error);
      toast.error('Failed to load announcement status');
    }
  };

  const handleFileUpload = async (filename, file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.includes('audio')) {
      toast.error('Please select an audio file (MP3)');
      return;
    }

    setUploading(prev => ({ ...prev, [filename]: true }));

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('filename', filename);

      await axios.post(`${API}/superadmin/upload-announcement`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Uploaded ${filename} successfully`);
      await loadFileStatus();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(`Failed to upload ${filename}`);
    } finally {
      setUploading(prev => ({ ...prev, [filename]: false }));
    }
  };

  return (
    <div className="space-y-2">
      {ANNOUNCEMENT_FILES.map(file => {
        const status = fileStatus[file.id];
        const isUploaded = status?.uploaded && !status?.isPlaceholder;
        const isPlaceholder = status?.isPlaceholder;
        const isUploading = uploading[file.id];

        return (
          <div 
            key={file.id}
            className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
          >
            <div className="flex items-center gap-3">
              {isUploaded ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : isPlaceholder ? (
                <AlertCircle className="w-5 h-5 text-yellow-600" />
              ) : (
                <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
              )}
              <div>
                <div className="font-medium text-sm">{file.label}</div>
                <div className="text-xs text-gray-500">
                  {file.id}
                  {isPlaceholder && (
                    <span className="ml-2 text-yellow-600">(using placeholder beep)</span>
                  )}
                </div>
              </div>
            </div>
            
            <div>
              <input
                type="file"
                id={`file-${file.id}`}
                accept="audio/mp3,audio/mpeg"
                className="hidden"
                onChange={(e) => {
                  const selectedFile = e.target.files[0];
                  if (selectedFile) {
                    handleFileUpload(file.id, selectedFile);
                  }
                  e.target.value = '';
                }}
              />
              <Button
                onClick={() => document.getElementById(`file-${file.id}`).click()}
                disabled={isUploading}
                size="sm"
                variant={isUploaded ? "outline" : "default"}
              >
                <Upload className="w-4 h-4 mr-2" />
                {isUploading ? 'Uploading...' : isUploaded ? 'Replace' : 'Upload'}
              </Button>
            </div>
          </div>
        );
      })}

      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Files with <AlertCircle className="w-4 h-4 inline text-yellow-600" /> are using placeholder beep sounds. 
          Upload actual MP3 files for proper announcements.
        </p>
      </div>
    </div>
  );
}
